import { BoReference } from '../../bos/bo-reference.js'
import { BoVisitor } from '../../bos/bo-visitor.js'
import type { NameAndType } from '../../types.js'
import type { TsCodeType } from '../../types.js'
import type { TsTypeType } from '../../types.js'
import { jsonProperty, jsonClass } from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { TrackByFunctionNames, TrackByFunctions } from '../logic/for-block.js'
import {
	BlockWithDataSource,
	BlockWithPagedDataSource,
} from './block-with-data-source.js'
import { Expression } from '../../data/text-or-code.js'
import { ExpressionOrCode } from '../../data/text-or-code.js'
import { TypeReference } from '../../data/type-reference.js'
import { Screen } from '../../bos/screen.js'

export const BlockDataSources = {
	entity: 'Entity on ServerDataStore',
	array: 'Array of items',
	tasks: 'Process tasks',
	function: 'Function call',
} as const
export type BlockDataSource = keyof typeof BlockDataSources

@jsonClass('BlockDataSourceDefinition')
export class BlockDataSourceDefinition {
	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 1,
		label: 'Data source type',
		fieldset: () => 'Data source',
		options: Object.entries(BlockDataSources),
		isMandatory: () => true,
	})
	sourceType: BlockDataSource = 'entity'

	@jsonProperty(BoReference)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'boRef',
		order: 10,
		label: 'Entity',
		fieldset: () => 'Data source',
		allowedBoTypes: ['Entity'],
		includeImportedModules: true,
		// isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'entity',
		isMandatory: () => true,
	})
	entityRef: BoReference = new BoReference()

	@jsonProperty()
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'boRefDetail',
		order: 11,
		label: 'Entity subset',
		detailName: 'subTypes',
		fieldset: () => 'Data source',
		boRef: (blocks) => blocks[0].dataSource.entityRef,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'entity',
		isMandatory: () => true,
	})
	entitySubsetName: string = ''

	@jsonProperty(BoReference)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'boRef',
		order: 12,
		label: 'ServerDataStore',
		fieldset: () => 'Data source',
		allowedBoTypes: ['ServerDataStore'],
		includeImportedModules: true,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'entity',
		isMandatory: () => true,
	})
	serverDataStoreRef: BoReference = new BoReference()

	@jsonProperty(ExpressionOrCode)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'code',
		order: 13,
		label: 'Find options',
		fieldset: () => 'Data source',
		codeLanguage: 'ts',
		tsReturnType: (blocks) =>
			`Sys.Types.FindManyOptions<${blocks[0]?.dataSource.getItemTsType()}>`,
		editorSize: 'normal',
		spanColumns: 'span 2',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'entity',
	})
	additionalFindOptionsCode = new ExpressionOrCode('{}')

	@jsonProperty(Expression)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'code',
		order: 20,
		label: 'Item type',
		fieldset: () => 'Data source',
		codeLanguage: 'ts',
		tsReturnType: () => 'any',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) =>
			blocks[0]?.dataSource.sourceType == 'array' ||
			blocks[0]?.dataSource.sourceType == 'function',
		isMandatory: () => true,
	})
	itemTsType = new Expression('{}')

	@jsonProperty(ExpressionOrCode)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'code',
		order: 21,
		label: 'Array of items',
		fieldset: () => 'Data source',
		codeLanguage: 'ts',
		tsReturnType: (blocks) => {
			const type = `${blocks[0]?.dataSource.itemTsType.content}`
			return `(${type})[] | ((paging: Sys.Types.PagingDefinition) => (${type})[] | Promise<${type}[]>)`
		},
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'array',
		isMandatory: () => true,
	})
	collectionCode = new ExpressionOrCode('[]')

	@jsonProperty(Boolean)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'checkbox',
		order: 40,
		label: 'Include tasks currently assigned to the user',
		fieldset: () => 'Process Tasks',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'tasks',
		isMandatory: () => true,
	})
	includeAssignedTasks: boolean = true

	@jsonProperty(Boolean)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'checkbox',
		order: 40,
		label: 'Include tasks the user is allowed to take over',
		fieldset: () => 'Process Tasks',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'tasks',
	})
	includeUserAllowedTasks: boolean = true

	@jsonProperty(Boolean)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'checkbox',
		order: 40,
		label: "Include tasks the user's roles are allowed to take over",
		fieldset: () => 'Process Tasks',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'tasks',
	})
	includeRoleAllowedTasks: boolean = false

	@jsonProperty(Boolean)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'checkbox',
		order: 40,
		label: "Include tasks the user's groups are allowed to take over",
		fieldset: () => 'Process Tasks',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'tasks',
	})
	includeGroupAllowedTasks: boolean = false

	@jsonProperty(ExpressionOrCode)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'code',
		order: 21,
		label: 'Function to call',
		fieldset: () => 'Data source',
		codeLanguage: 'ts',
		tsReturnType: () => '(query: string) => any[]',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.sourceType == 'function',
		isMandatory: () => true,
	})
	functionCallCode = new ExpressionOrCode('')

	@jsonProperty(String)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'input',
		order: 30,
		label: 'Loop variable',
		fieldset: () => 'Data source',
		contentType: 'text',
		elementType: 'input',
	})
	loopVariableDefinition: TsTypeType = 'item'

	@jsonProperty(String)
	@ScreenEditorInput<BlockWithDataSource>({
		inputType: 'input',
		order: 31,
		label: 'Index variable (0, 1, 2, ...)',
		fieldset: () => 'Data source',
		contentType: 'text',
		elementType: 'input',
	})
	indexVariableDefinition: TsTypeType = ''

	@jsonProperty(String)
	@ScreenEditorInput<BlockWithPagedDataSource>({
		inputType: 'code',
		order: 41,
		label: (blocks) =>
			blocks[0]?.dataSource.usePaging ? 'Page size' : 'Max row count',
		tab: 'Data Management',
		fieldset: () => 'Data source',
		codeLanguage: 'ts',
		tsReturnType: () => `number`,
		editorSize: 'singleline',
		isBinding: false,
	})
	pageSizeCode: TsCodeType = '20'

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 7,
		tab: 'Data Management',
		fieldset: () => 'Data source',
		label: 'Track by',
		options: TrackByFunctions.map((f) => [f, TrackByFunctionNames[f]]),
	})
	trackBy: (typeof TrackByFunctions)[number] = 'trackByAutomatic'

	getItemTsType() {
		switch (this.sourceType) {
			case 'entity':
				if (!this.entityRef.isFullyDefined()) return 'any'
				return this.entityRef.getQualifiedName(this.entitySubsetName)

			case 'array':
			case 'function':
				return this.itemTsType.content

			case 'tasks':
				return 'Sys.Types.UserTaskToken'
		}
	}

	acceptVariableString(variable: NameAndType, screen: Screen) {
		this.sourceType = 'array'
		this.collectionCode = new ExpressionOrCode(variable.name, 'E')
		this.itemTsType = new Expression(
			TypeReference.adaptTypeForThisModule(variable.type, screen.moduleId),
		)
	}

	getIndexVariableName() {
		return (
			this.indexVariableDefinition?.trim() ||
			this.loopVariableDefinition + 'Index'
		)
	}
}

@jsonClass('PagedBlockDataSourceDefinition')
export class PagedBlockDataSourceDefinition extends BlockDataSourceDefinition {
	@jsonProperty()
	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 40,
		label: 'Use paging',
		tab: 'Data Management',
	})
	usePaging: boolean = true

	@jsonProperty(Expression)
	@ScreenEditorInput<BlockWithPagedDataSource>({
		inputType: 'code',
		order: 42,
		label: 'Page size options',
		tab: 'Data Management',
		codeLanguage: 'ts',
		tsReturnType: () => `number[]`,
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => blocks[0]?.dataSource.usePaging,
	})
	pageSizeOptionsCode = new Expression('[10, 20, 50]')
}
